/**
 * Kostenblatt — Auslagen-Erfassung pro Verwaltervertrag
 * =====================================================
 * Erfasst Porto, Fahrten, Kopien, Behördengebühren und sonstige Auslagen.
 * Bietet Quartals-/Jahresübersicht und schnelle Fahrt-Erfassung.
 * Backend: /api/management-costs
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, X, Car, Envelope, Coins, Printer, PhoneCall, ShieldCheck, DotsThree, Trash } from "@phosphor-icons/react";

const CATEGORIES = [
  { key: "porto",     label: "Porto",           icon: Envelope,    color: "text-blue-700 bg-blue-50" },
  { key: "fahrt",     label: "Fahrtkosten",     icon: Car,          color: "text-emerald-700 bg-emerald-50" },
  { key: "kopie",     label: "Kopien / Druck",  icon: Printer,     color: "text-slate-700 bg-slate-50" },
  { key: "telefon",   label: "Telefon",         icon: PhoneCall,   color: "text-purple-700 bg-purple-50" },
  { key: "behoerde",  label: "Behörde",         icon: ShieldCheck, color: "text-amber-700 bg-amber-50" },
  { key: "sonstiges", label: "Sonstiges",       icon: DotsThree,   color: "text-slate-700 bg-slate-100" },
];
const catMeta = (k) => CATEGORIES.find((c) => c.key === k) || CATEGORIES[5];
const eur = (v) => (v == null ? "—" : `${Number(v).toFixed(2).replace(".", ",")} €`);

export default function Kostenblatt() {
  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [tripOpen, setTripOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/management-contracts").then((r) => {
      const list = r.data || [];
      setContracts(list);
      if (!contractId && list.length) setContractId(list[0].id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (!contractId) return;
    try {
      const [it, sm] = await Promise.all([
        api.get("/management-costs", { params: { contract_id: contractId, year } }),
        api.get("/management-costs/summary", { params: { contract_id: contractId, year } }),
      ]);
      setItems(it.data || []);
      setSummary(sm.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Laden");
    }
  };
  useEffect(() => { if (contractId) load(); /* eslint-disable-next-line */ }, [contractId, year]);

  const contract = useMemo(() => contracts.find((c) => c.id === contractId), [contracts, contractId]);

  const remove = async (id) => {
    if (!await window._domivusConfirm("Position wirklich löschen?")) return;
    await api.delete(`/management-costs/${id}`);
    toast.success("Gelöscht");
    load();
  };

  return (
    <div className="space-y-4" data-testid="kostenblatt-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Coins size={22} className="text-emerald-700" /> Kostenblatt
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Porto, Fahrten, Auslagen · pro Verwaltervertrag mit Jahresübersicht
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={contractId || "__none__"} onValueChange={(v) => v !== "__none__" && setContractId(v)}>
            <SelectTrigger className="w-72 h-9 text-sm" data-testid="kb-contract-select">
              <SelectValue placeholder="Vertrag wählen" />
            </SelectTrigger>
            <SelectContent>
              {contracts.filter((c) => c && c.id).map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.title || c.contract_number || c.id.slice(0, 8)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-24 h-9 text-sm" data-testid="kb-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[0, 1, 2, 3, 4].map((n) => {
                const y = new Date().getFullYear() - n;
                return <SelectItem key={y} value={String(y)}>{y}</SelectItem>;
              })}
            </SelectContent>
          </Select>
          <Button onClick={() => { setEditing(null); setAddOpen(true); }} className="h-9 gap-1.5 bg-slate-900 hover:bg-slate-800" data-testid="kb-add-btn">
            <Plus size={14} /> Position
          </Button>
          <Button variant="outline" onClick={() => setTripOpen(true)} className="h-9 gap-1.5" data-testid="kb-trip-btn">
            <Car size={14} /> Fahrt
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="kb-summary">
          <SummaryCard label="Gesamt (Brutto)" value={eur(summary.total_gross)} highlight />
          <SummaryCard label="Weiterberechenbar" value={eur(summary.total_billable_gross)} />
          <SummaryCard label="Positionen" value={summary.total_count} />
          {[1, 2, 3, 4].map((q) => (
            <SummaryCard key={q} label={`Q${q}`} value={eur(summary.by_quarter[q]?.gross || 0)} sub={`${summary.by_quarter[q]?.count || 0} Pos.`} />
          ))}
        </div>
      )}

      {/* Category Breakdown */}
      {summary && Object.keys(summary.by_category || {}).length > 0 && (
        <div className="border border-slate-200 rounded-md p-3 bg-white">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Nach Kategorie</div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {CATEGORIES.map((c) => {
              const s = summary.by_category[c.key];
              if (!s) return null;
              const Icon = c.icon;
              return (
                <div key={c.key} className={`rounded-md px-2 py-1.5 ${c.color}`}>
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold">
                    <Icon size={12} /> {c.label}
                  </div>
                  <div className="text-sm font-mono">{eur(s.gross)}</div>
                  <div className="text-[10px] opacity-75">{s.count} Pos.</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Items Table */}
      <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-600 w-24">Datum</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-600 w-28">Kategorie</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-600">Beschreibung</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-slate-600 w-24">Netto</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-slate-600 w-24">Brutto</th>
              <th className="px-3 py-2 text-center text-[10px] uppercase tracking-wider text-slate-600 w-20">Weiterber.</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center text-sm text-slate-500 py-6">Keine Positionen für {year}.</td></tr>
            )}
            {items.map((it) => {
              const c = catMeta(it.category);
              const Icon = c.icon;
              return (
                <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`kb-row-${it.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{it.date}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${c.color}`}>
                      <Icon size={10} /> {c.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    {it.description || "—"}
                    {it.source === "auto_post" && <span className="ml-2 text-[9px] px-1 py-0.5 bg-blue-100 text-blue-800 rounded">AUTO-PORTO</span>}
                    {it.source === "auto_trip" && <span className="ml-2 text-[9px] px-1 py-0.5 bg-emerald-100 text-emerald-800 rounded">AUTO-FAHRT</span>}
                    {it.km && <span className="ml-2 text-[10px] text-slate-500">({it.km} km × {eur(it.rate_per_km)})</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{eur(it.amount_net)}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{eur(it.amount_gross)}</td>
                  <td className="px-3 py-2 text-center">{it.is_billable_to_owner ? "✓" : "—"}</td>
                  <td className="px-2 py-2 text-center">
                    <button onClick={() => remove(it.id)} className="text-rose-600 hover:text-rose-800 p-1" data-testid={`kb-del-${it.id}`}>
                      <Trash size={12} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {addOpen && (
        <CostDialog
          open={addOpen}
          contract={contract}
          editing={editing}
          onClose={() => { setAddOpen(false); setEditing(null); }}
          onSaved={() => { setAddOpen(false); setEditing(null); load(); }}
        />
      )}
      {tripOpen && (
        <TripDialog
          open={tripOpen}
          contract={contract}
          onClose={() => setTripOpen(false)}
          onSaved={() => { setTripOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, sub, highlight }) {
  return (
    <div className={`rounded-md border ${highlight ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"} p-2.5`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-lg font-semibold text-slate-900 mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}

function CostDialog({ open, contract, editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing || {
    contract_id: contract?.id,
    date: new Date().toISOString().slice(0, 10),
    category: "sonstiges",
    description: "",
    amount_net: "",
    vat_rate: 19,
    is_billable_to_owner: true,
    property_id: "",
  });
  const [properties, setProperties] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const ids = contract?.property_ids || (contract?.property_assignments || []).map((a) => a.property_id);
    if (ids?.length) {
      api.get("/properties").then((r) => {
        setProperties((r.data || []).filter((p) => ids.includes(p.id)));
      }).catch(() => {});
    }
  }, [contract]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        contract_id: contract.id,
        property_id: form.property_id || null,
        date: form.date,
        category: form.category,
        description: form.description,
        amount_net: Number(form.amount_net) || 0,
        vat_rate: Number(form.vat_rate) || 0,
        is_billable_to_owner: !!form.is_billable_to_owner,
      };
      await api.post("/management-costs", payload);
      toast.success("Position gespeichert");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="kb-add-dialog">
        <DialogHeader>
          <DialogTitle>Neue Position</DialogTitle>
          <DialogDescription>Erfasse eine Auslage für „{contract?.title}“</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Datum</Label>
              <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="h-8" data-testid="kb-date" />
            </div>
            <div>
              <Label className="text-xs">Kategorie</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="h-8 text-sm" data-testid="kb-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Beschreibung</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="text-sm min-h-[60px]" data-testid="kb-desc" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Betrag Netto (€)</Label>
              <Input type="number" step="0.01" value={form.amount_net} onChange={(e) => set("amount_net", e.target.value)} className="h-8 font-mono" data-testid="kb-net" />
            </div>
            <div>
              <Label className="text-xs">USt. (%)</Label>
              <Input type="number" step="0.1" value={form.vat_rate} onChange={(e) => set("vat_rate", e.target.value)} className="h-8" data-testid="kb-vat" />
            </div>
            <div className="flex items-end">
              <label className="text-xs flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={form.is_billable_to_owner} onChange={(e) => set("is_billable_to_owner", e.target.checked)} data-testid="kb-billable" />
                Weiterberechenbar
              </label>
            </div>
          </div>
          {properties.length > 1 && (
            <div>
              <Label className="text-xs">Objekt (optional)</Label>
              <Select value={form.property_id || "__none__"} onValueChange={(v) => set("property_id", v === "__none__" ? "" : v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Objekt wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— nicht zugeordnet —</SelectItem>
                  {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.property_number} · {p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="kb-save">Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TripDialog({ open, contract, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    km: "",
    rate_per_km: 0.30,
    description: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const total = (Number(form.km) || 0) * (Number(form.rate_per_km) || 0);
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/management-costs/trips", {
        contract_id: contract.id,
        date: form.date,
        km: Number(form.km) || 0,
        rate_per_km: Number(form.rate_per_km) || 0.30,
        description: form.description,
      });
      toast.success("Fahrt erfasst");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="kb-trip-dialog">
        <DialogHeader>
          <DialogTitle>Fahrt erfassen</DialogTitle>
          <DialogDescription>Kilometer × Satz — für „{contract?.title}“</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Datum</Label>
              <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="h-8" data-testid="kb-trip-date" />
            </div>
            <div>
              <Label className="text-xs">Zweck (optional)</Label>
              <Input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Objekttermin, Besichtigung, …" className="h-8" data-testid="kb-trip-desc" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Kilometer</Label>
              <Input type="number" step="1" value={form.km} onChange={(e) => set("km", e.target.value)} className="h-8 font-mono" data-testid="kb-trip-km" placeholder="42" />
            </div>
            <div>
              <Label className="text-xs">Satz (€/km)</Label>
              <Input type="number" step="0.05" value={form.rate_per_km} onChange={(e) => set("rate_per_km", e.target.value)} className="h-8 font-mono" data-testid="kb-trip-rate" />
            </div>
          </div>
          <div className="p-2 bg-emerald-50 border border-emerald-200 rounded text-sm">
            <span className="text-slate-600">Berechnet:</span>{" "}
            <span className="font-mono font-semibold">{total.toFixed(2).replace(".", ",")} €</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={saving || !form.km} className="bg-emerald-600 hover:bg-emerald-700" data-testid="kb-trip-save">Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
